import {
  ChangeEvent,
  memo,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import { Box, Checkbox, SystemStyleObject, useSize } from "@hightouch/htui";

import { Private } from "src/components/private";
import { SortMenu } from "src/components/sort-menu";
import { usePermissionContext } from "src/components/permission/permission-context";
import { OrderBy } from "src/graphql";
import { SortOption } from "src/ui/table/use-table-config";
import { Cell, HeaderCell, TableCell } from "./cells";
import { Placeholder, PlaceholderContent } from "./placeholder";
import { Row } from "./row";
import { DefaultPageContainerPadding } from "src/components/layout/page-container";
import { SkeletonContextProvider, maskCss } from "src/components/skeletons";

export type TableColumnVariant = "numeric";

export type TableColumn<RowData extends Record<string, any> = any> = {
  name?: string;
  header?: () => ReactElement;
  headerSx?: SystemStyleObject;
  cell: (
    row: RowData,
    index: number,
    selected: boolean,
  ) => ReactNode | ReactElement;
  cellSx?:
    | SystemStyleObject
    | ((row: RowData) => SystemStyleObject | undefined);
  min?: string;
  max?: string;
  whitespace?: "unset" | undefined;
  divider?: boolean;
  disabled?: (row: RowData) => boolean | string;
  sortDirection?: OrderBy | null;
  onClick?(): void;
  breakpoint?: "lg" | "md" | "sm";
  variant?: TableColumnVariant;

  // Show a custom skeleton component when loading
  skeleton?: ReactElement;
};

export type RowClickHandler<RowData extends Record<string, any>> = (
  row: RowData,
  event: MouseEvent,
) => unknown;

export type TableProps<RowData extends Record<string, any>> = {
  columns: Array<TableColumn<RowData>>;
  data: Array<RowData> | undefined;
  onRowClick?: RowClickHandler<RowData>;
  onSelect?: (key: any) => void;
  selectedRows?: Array<any>;
  width?: string;
  defaultMin?: string;
  defaultMax?: string;
  error?: boolean;
  placeholder?: PlaceholderContent;
  primaryKey?: keyof RowData;
  /**
   * A function returning `true` or a truthy string on a given row will disable the row.
   * If the row is selectable, the string will be displayed as a tooltip on the checkbox
   */
  disabled?: (row: RowData) => boolean | string;
  /**
   * Controls whether a row's checkbox is enabled without affecting row opacity or click handling.
   * `true` → selectable (default when omitted), `false` → checkbox disabled, `string` → disabled with tooltip.
   */
  selectable?: (row: RowData) => boolean | string;
  headerHeight?: string;
  rowHeight?: string;
  /** Rows to tint as pointed-at, which is not the same as selected: no
   *  checkbox is ticked and each cell keeps its own background. A list is for a
   *  highlight that comes from outside the table, where one hovered thing can
   *  address several rows. */
  highlight?: number | string | ReadonlyArray<number | string>;
  /** Row hover, for a surface that mirrors the hovered row somewhere else. */
  onRowMouseEnter?: (row: RowData) => void;
  onRowMouseLeave?: (row: RowData) => void;
  loading?: boolean;
  showHeaders?: boolean;
  sortingEnabled?: boolean;
  scrollable?: boolean;
  scrollableX?: boolean;
  scrollableY?: boolean;
  top?: number | string;
  /** @deprecated use sort from useTableConfig */
  sortOptions?: SortOption<any>[];
  isDense?: boolean;
  isPrivate?: boolean;
  alignFirstHeader?: boolean;
  backgroundColor?: string;
  renderExpandedRow?: (row: RowData, rowIndex: number) => ReactNode;
  hideLastRowBorder?: boolean;
};

export function Table<RowData extends Record<string, any>>({
  primaryKey = "id",
  columns: propColumns,
  data,
  onRowClick,
  onSelect,
  onRowMouseEnter,
  onRowMouseLeave,
  selectedRows,
  width = "100%",
  defaultMin = "0",
  defaultMax = "1fr",
  placeholder,
  error,
  headerHeight = "52px",
  rowHeight,
  disabled,
  selectable,
  highlight,
  loading,
  sortingEnabled = true,
  showHeaders = true,
  scrollable = false,
  scrollableX = false,
  scrollableY = false,
  isDense = false,
  top,
  sortOptions,
  isPrivate = false,
  alignFirstHeader = false,
  backgroundColor = "white",
  renderExpandedRow,
  hideLastRowBorder = false,
}: Readonly<TableProps<RowData>>) {
  const isEmpty = !loading && data?.length === 0;
  const permission = usePermissionContext();

  const supportsSelection =
    Boolean(selectedRows && onSelect) && !permission?.unauthorized;
  const tableRef = useRef<HTMLDivElement | null>(null);
  const measuredWidth = useSize(tableRef)?.width;
  const sortable = Boolean(sortOptions && sortOptions.length > 0);
  const expandable = Boolean(renderExpandedRow);

  // `useSize` allocates a new object per ResizeObserver tick and reports height
  // as well as width, so `columns` depends on the crossed breakpoints instead
  // to stay referentially stable. Width is unknown until the first measurement.
  const isAboveLg = measuredWidth === undefined || measuredWidth > 1400;
  const isAboveMd = measuredWidth === undefined || measuredWidth > 1000;
  const isAboveSm = measuredWidth === undefined || measuredWidth > 600;

  const columns = useMemo(() => {
    return propColumns.filter((column) => {
      switch (column.breakpoint) {
        case "lg":
          return isAboveLg;
        case "md":
          return isAboveMd;
        case "sm":
          return isAboveSm;
        default:
          return true;
      }
    });
  }, [propColumns, isAboveLg, isAboveMd, isAboveSm]);

  const placeholderRows = useMemo(
    () => createPlaceholderData(primaryKey),
    [primaryKey],
  );

  const rows = useMemo(() => {
    const rowData = loading ? placeholderRows : data;
    return rowData?.map((row, index: number) => {
      const isLastRow = index === rowData.length - 1;
      return (
        <MemoizedTableRow
          // Rows without a `primaryKey` value carry no identity, so position is
          // the only key available. Pass `primaryKey` to key rows by their id.
          key={row[primaryKey] ?? index}
          columns={columns as TableColumn<Record<string, any>>[]}
          disabled={disabled ? disabled(row) : false}
          selectable={selectable ? selectable(row) : true}
          height={isDense ? "auto" : rowHeight}
          index={index}
          primaryKey={primaryKey as any}
          row={row}
          selected={Boolean(selectedRows?.includes(row[primaryKey]))}
          isHighlighted={isRowHighlighted(highlight, row[primaryKey])}
          onClick={onRowClick as any}
          onMouseEnter={onRowMouseEnter as any}
          onMouseLeave={onRowMouseLeave as any}
          onSelect={
            supportsSelection && onSelect ? (onSelect as any) : undefined
          }
          sortable={sortable}
          isDense={isDense}
          renderExpandedRow={loading ? undefined : (renderExpandedRow as any)}
          isLastRow={isLastRow && hideLastRowBorder}
        />
      );
    });
  }, [
    data,
    placeholderRows,
    columns,
    highlight,
    onRowClick,
    onRowMouseEnter,
    onRowMouseLeave,
    onSelect,
    disabled,
    selectable,
    selectedRows,
    primaryKey,
    rowHeight,
    supportsSelection,
    isDense,
    renderExpandedRow,
    loading,
    sortable,
    hideLastRowBorder,
  ]);

  const headerCells = useMemo(() => {
    return columns.map(
      (
        {
          header,
          headerSx,
          name,
          sortDirection,
          onClick,
          divider,
          variant,
        }: TableColumn,
        index,
      ) => {
        const key = name ? name : `header-column-${index}-cell`;
        const sortingProps = sortingEnabled
          ? {
              sortDirection,
              onClick,
            }
          : {};

        return (
          <HeaderCell
            key={key}
            top={top}
            isDense={isDense}
            divider={divider}
            {...sortingProps}
            alignFirstHeader={alignFirstHeader}
            backgroundColor={backgroundColor}
            sx={headerSx}
            height={headerHeight}
            variant={variant}
            isLoading={loading}
          >
            {header ? header() : name}
          </HeaderCell>
        );
      },
    );
  }, [
    backgroundColor,
    columns,
    top,
    isDense,
    loading,
    sortingEnabled,
    alignFirstHeader,
    headerHeight,
  ]);

  const canToggleRow = useCallback(
    (row: RowData) => {
      if (disabled?.(row)) return false;
      if (selectable && selectable(row) !== true) return false;
      return true;
    },
    [disabled, selectable],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const onSelectAll = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (typeof onSelect !== "function") {
        return;
      }

      const selectedUntoggleableRows =
        data?.filter(
          (row) =>
            !canToggleRow(row) && selectedRows?.includes(row[primaryKey]),
        ) ?? [];

      if (event.target.checked) {
        onSelect(
          [
            ...(data?.filter(canToggleRow) ?? []),
            ...selectedUntoggleableRows,
          ].map((row) => row[primaryKey]),
        );
      } else {
        onSelect(selectedUntoggleableRows.map((row) => row[primaryKey]));
      }
    },
    [data, onSelect, primaryKey, canToggleRow],
  );

  const isPlaceholder = isEmpty || error;

  // Only the select-all checkbox reads these; the Set keeps it O(rows).
  const { enabledRowCount, enabledSelectedRowCount } = useMemo(() => {
    if (!supportsSelection || !data) {
      return { enabledRowCount: 0, enabledSelectedRowCount: 0 };
    }

    const selectedKeys = new Set(selectedRows);
    let enabled = 0;
    let enabledSelected = 0;

    for (const row of data) {
      if (!canToggleRow(row)) continue;
      enabled++;
      if (selectedKeys.has(row[primaryKey])) enabledSelected++;
    }

    return {
      enabledRowCount: enabled,
      enabledSelectedRowCount: enabledSelected,
    };
  }, [supportsSelection, data, selectedRows, canToggleRow, primaryKey]);

  return (
    <SkeletonContextProvider isLoading={Boolean(loading)}>
      <Private isDisabled={!isDense || !isPrivate}>
        <Box
          ref={tableRef as any}
          flex={isPlaceholder ? 1 : undefined}
          height={isPlaceholder ? "100%" : undefined}
          as="table"
          overflow={isPlaceholder ? "hidden" : undefined}
          bg={backgroundColor}
          display={isPlaceholder ? "flex" : "grid"}
          p={isPlaceholder ? placeholder?.p : undefined}
          flexDirection="column"
          gridTemplateColumns={getGridTemplateColumns(
            [
              expandable && { max: "max-content" },
              supportsSelection && { min: "max-content", max: "max-content" },
              ...columns,
              sortable && { max: "max-content" },
            ].filter(Boolean),
            scrollable || scrollableY ? "min-content" : defaultMin,
            defaultMax,
          )}
          width={width}
          overflowX={scrollable || scrollableX ? "auto" : undefined}
          tabIndex={scrollable || scrollableX ? 0 : undefined} // Scrollable tables require keyboard access
          sx={{
            [`td:nth-child(1)`]: { pl: isDense ? undefined : 6 },
            [`td:nth-child(${columns.length + (expandable ? 2 : 1)}n)`]: {
              pr: isDense ? undefined : 6,
            },
            mask: !error && loading ? maskCss : undefined,
          }}
          // While loading, nothing should be clickable.
          // This also prevents hover styles from being triggered.
          pointerEvents={loading ? "none" : undefined}
        >
          {showHeaders && !isPlaceholder && (
            <Box as="thead" display="contents">
              <Box as="tr" display="contents">
                {expandable && <HeaderCell>{null}</HeaderCell>}
                {supportsSelection && (
                  <HeaderCell top={top} isLoading={loading}>
                    <Checkbox
                      isChecked={Boolean(
                        data &&
                          data?.length > 0 &&
                          enabledSelectedRowCount === enabledRowCount,
                      )}
                      isIndeterminate={Boolean(
                        data &&
                          data?.length > 0 &&
                          (selectedRows?.length ?? 0) > 0 &&
                          enabledSelectedRowCount < enabledRowCount,
                      )}
                      onChange={onSelectAll}
                    />
                  </HeaderCell>
                )}
                {headerCells}
                {sortable && (
                  <HeaderCell isLoading={loading} top={top} isSortMenu>
                    <SortMenu options={sortOptions} />
                  </HeaderCell>
                )}
              </Box>
            </Box>
          )}

          <Box as="tbody" display="contents" position="relative">
            {isEmpty || error ? (
              <Box as="tr" display="contents" position="relative">
                <Box
                  as="td"
                  colSpan={columns.length + 1}
                  height="100%"
                  display="flex"
                  alignItems="center"
                  p={`${DefaultPageContainerPadding.X} !important`}
                >
                  <Box width="100%" height="100%">
                    <Placeholder content={placeholder} error={Boolean(error)} />
                  </Box>
                </Box>
              </Box>
            ) : (
              rows
            )}
          </Box>
        </Box>
      </Private>
    </SkeletonContextProvider>
  );
}

const getGridTemplateColumns = (columns, defaultMin, defaultMax) =>
  columns
    .map(({ min, max }) => `minmax(${min || defaultMin}, ${max || defaultMax})`)
    .join(" ");

type TableRowProps<RowData extends Record<string, any>> = {
  row: RowData;
  primaryKey: any;
  selected: boolean;
  isHighlighted: boolean;
  disabled?: boolean | string;
  selectable?: boolean | string;
  height?: string;
  onClick?: RowClickHandler<RowData>;
  onSelect?: (row: RowData) => unknown;
  onMouseEnter?: (row: RowData) => void;
  onMouseLeave?: (row: RowData) => void;
  columns: TableColumn<RowData>[];
  index: number;
  sortable: boolean;
  isDense: boolean;
  renderExpandedRow?: (row: RowData, rowIndex: number) => ReactNode;
  isLastRow?: boolean;
};

function TableRow<RowData extends Record<string, any>>({
  row,
  disabled,
  selectable,
  selected,
  isHighlighted,
  height,
  columns,
  onClick,
  onSelect,
  onMouseEnter,
  onMouseLeave,
  sortable,
  isDense,
  index: rowIndex,
  primaryKey,
  renderExpandedRow,
  isLastRow = false,
}: TableRowProps<RowData>) {
  const isDisabled = Boolean(disabled);

  const [isExpanded, setIsExpanded] = useState(false);
  const expandedContent =
    renderExpandedRow && isExpanded ? renderExpandedRow(row, rowIndex) : null;

  return (
    <>
      <Row
        primaryKey={primaryKey}
        clickable={Boolean(onClick && !isDisabled)}
        disabled={disabled}
        selectable={selectable}
        row={row}
        selected={selected}
        isHighlighted={isHighlighted}
        onClick={!isDisabled ? onClick : undefined}
        onSelect={onSelect}
        onMouseEnter={onMouseEnter ? () => onMouseEnter(row) : undefined}
        onMouseLeave={onMouseLeave ? () => onMouseLeave(row) : undefined}
        sortable={sortable}
        isExpanded={isExpanded}
        setIsExpanded={renderExpandedRow ? setIsExpanded : undefined}
        rowIndex={rowIndex}
      >
        {columns.map((column: TableColumn, index) => (
          <TableCell
            isDense={isDense}
            key={column.name ? column.name : `column-${index}-cell`}
            rowIndex={rowIndex}
            column={column}
            height={height}
            selected={selected}
            row={row}
            isLastRow={isLastRow}
          />
        ))}
      </Row>
      {expandedContent && (
        <Box as="tr" display="contents">
          <Cell
            sx={{
              // This lets the cell span the entire width of the table.
              gridColumn: `1/-1`,
              bg: "base.lightBackground",
              px: 8,
              py: 6,
            }}
          >
            {expandedContent}
          </Cell>
        </Box>
      )}
    </>
  );
}

const MemoizedTableRow = memo(TableRow);

function isRowHighlighted(
  highlight: TableProps<any>["highlight"],
  key: unknown,
): boolean {
  if (highlight === undefined) return false;
  if (Array.isArray(highlight)) return highlight.some((entry) => entry === key);
  return highlight === key;
}

const createPlaceholderData = (primaryKey: PropertyKey) =>
  Array.from({ length: 10 }).map((_, index) => ({ [primaryKey]: index }));
